import React, { useState, useEffect } from "react";
import { Search, Filter, ChevronDown, MoreVertical } from "lucide-react";
import { getTickets } from "../../Services/TicketService";
import NotificationAlert from "../Alerts/NotificationAlert";
import DropdownPortal from "../DropDownPortal";
import ApproveTicketModal from "../Modal/ApproveTicketModal";
import RejectTicketModal from "../Modal/RejectTicketModal";
import AssignTicketModal from "../Modal/AssignTicketModal";
import HoldTicketModal from "../Modal/HoldTicketModal";
import ViewTicketModal from "../Modal/ViewTicketModal";
import TicketHistoryModal from "../Modal/TicketHistoryModal";
import FilterTableModal from "../Modal/FilterTableModal";
import { ChevronLeft, ChevronRight } from "lucide-react";
 import { useNavigate } from "react-router-dom";

const getSeverityStyle = (severity) => {
  switch (severity) {
    case "High":
      return "bg-red-100 text-red-700";
    case "Moderate":
      return "bg-yellow-100 text-yellow-700";
    case "Low":
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const NewTickets = () => {
   const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [alert, setAlert] = useState(null);

  // Dropdown UI states
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // Modal States
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTicketData, setApproveTicketData] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTicketData, setRejectTicketData] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTicketData, setAssignTicketData] = useState(null);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdTicketData, setHoldTicketData] = useState(null);

  const [viewModal, setViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [historyModal, setHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  //Filter Modal States
  const [filterModal, setFilterModal] = useState(false);
  const [filterField, setFilterField] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [filters, setFilters] = useState({});
  const [filteredTickets, setFilteredTickets] = useState([]);

  //Search States
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  // Load Only New Tickets
  useEffect(() => {
    loadNewTickets(1);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters, filteredTickets]);

  const loadNewTickets = async () => {
    try {
      const response = await getTickets(1);
      const data = response.data;

      if (data.status === "Success") {
        const formatted = data.tickets.map((t, index) => ({
          id: index + 1,
          title: t.title,
          description: t.description,
          severity: t.severity,
          severityByPM: t.severityByPM,
          ticketType: t.ticketType,
          ticketUID: t.ticketUID,
          status: t.status,
          ticketNumber: t.ticketNumber,
          projectName: t.projectName,
          raisedByEmail: t.raisedByEmail,
          assignedBy: t.assignedBy,
          assignedTo: t.assignedTo,
          raisedAt: t.raisedAt,
          updated: formatDateTime(t.raisedAt),
          approvedAt: t.approvedAt,
          estimatedCompleteByDev: t.estimatedCompleteByDev,
          closeDate: t.closeDate,
          attachmentUrls: t.attachmentUrls || [],
          ...t,
        }));

        setTickets(formatted);
        setFilteredTickets(formatted);
      }
    } catch (err) {
       if (err.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: err.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      console.error("Error fetching tickets:", err);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  // Close action menu on outside click
  useEffect(() => {
    const closeMenu = () => setOpenActionMenu(null);
    window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, []);

  const getUniqueValues = (key) => {
    const values = tickets
      .map((t) => t[key])
      .filter((v) => v !== null && v !== undefined && v !== "");

    return [...new Set(values)];
  };

  const openFilter = (field) => {
    setFilterField(field);
    setFilterOptions(getUniqueValues(field));
    setFilterModal(true);
  };

  const applyColumnFilter = (field, selectedValues) => {
    const updated = { ...filters, [field]: selectedValues };
    setFilters(updated);

    let result = tickets;

    // Apply each active filter
    Object.keys(updated).forEach((key) => {
      if (updated[key].length > 0) {
        result = result.filter((row) => updated[key].includes(row[key]));
      }
    });

    // If search is active → apply search as well
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();

      result = result.filter((t) =>
        (
          t.title +
          " " +
          t.description +
          " " +
          t.projectName +
          " " +
          t.ticketType +
          " " +
          t.assignedTo +
          " " +
          t.status +
          " " +
          t.severity
        )
          .toLowerCase()
          .includes(q)
      );
    }

    setFilteredTickets(result);
  };

  //Search function
  const handleSearch = (query) => {
    setSearchQuery(query);

    let result = tickets;

    // Apply active filters first
    Object.keys(filters).forEach((key) => {
      if (filters[key].length > 0) {
        result = result.filter((row) => filters[key].includes(row[key]));
      }
    });

    // Now apply search
    if (query.trim() !== "") {
      const q = query.toLowerCase();

      result = result.filter((t) =>
        (
          t.title +
          " " +
          t.description +
          " " +
          t.projectName +
          " " +
          t.ticketType +
          " " +
          t.assignedTo +
          " " +
          t.status +
          " " +
          t.severity
        )
          .toLowerCase()
          .includes(q)
      );
    }

    setFilteredTickets(result);
  };

  const rowsToShow =
    searchQuery.trim() !== "" ||
    Object.values(filters).some((arr) => arr.length > 0)
      ? filteredTickets
      : tickets;

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const currentRows = rowsToShow.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(rowsToShow.length / rowsPerPage);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-2">
        <div className="flex flex-wrap items-center gap-3">
          {/* <h1 className="text-lg font-semibold text-gray-800">New Tickets</h1> */}

          <div className="relative">
            <Search
              className="absolute left-2 top-2.5 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search for tickets"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm border rounded-md"
            />
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
            <tr>
              <th className="p-3">Ticket No.</th>
              <th className="p-3">
                <div className="flex items-center gap-1">
                  Project Name
                  <Filter
                    size={14}
                    className="cursor-pointer text-gray-500 hover:text-black"
                    onClick={() => openFilter("projectName")}
                  />
                  {filters["projectName"]?.length > 0 && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {filters["projectName"].length}
                    </span>
                  )}
                </div>
              </th>
              {/* <th className="p-3">Last Updated</th> */}
              <th className="p-3">
                <div className="flex items-center gap-1">
                  Assigned To
                  <Filter
                    size={14}
                    className="cursor-pointer text-gray-500 hover:text-black"
                    onClick={() => openFilter("assignedTo")}
                  />
                  {filters["assignedTo"]?.length > 0 && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {filters["assignedTo"].length}
                    </span>
                  )}
                </div>
              </th>
              <th className="p-3">
                <div className="flex items-center gap-1">
                  Ticket Type
                  <Filter
                    size={14}
                    className="cursor-pointer text-gray-500 hover:text-black"
                    onClick={() => openFilter("ticketType")}
                  />
                  {filters["ticketType"]?.length > 0 && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {filters["ticketType"].length}
                    </span>
                  )}
                </div>
              </th>
              <th className="p-3">
                <div className="flex items-center gap-1">
                  Priority
                  <Filter
                    size={14}
                    className="cursor-pointer text-gray-500 hover:text-black"
                    onClick={() => openFilter("severity")}
                  />
                  {filters["severity"]?.length > 0 && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {filters["severity"].length}
                    </span>
                  )}
                </div>
              </th>
              <th className="p-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {currentRows.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-6 text-center text-gray-500">
                  No New tickets found.
                </td>
              </tr>
            ) : (
              currentRows.map((ticket) => (
                <tr
                  key={ticket.ticketNumber}
                  className="border-b hover:bg-gray-50 transition"
                >
                  <td
                    className="p-3 font-medium text-blue-600 cursor-pointer hover:underline"
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setViewModal(true);
                    }}
                  >
                    {ticket.ticketNumber}
                  </td>

                  <td className="p-3">{ticket.projectName}</td>
                  {/* <td className="p-3">{ticket.updated}</td> */}
                  <td className="p-3">{ticket.assignedTo || "--"}</td>
                  <td className="p-3">{ticket.ticketType}</td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityStyle(
                        ticket.severity
                      )}`}
                    >
                      {ticket.severity}
                    </span>
                  </td>

                  {/* ACTION BUTTON */}
                  <td className="p-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenu(
                          openActionMenu === ticket.id ? null : ticket.id
                        );
                        setMenuPosition({ x: e.clientX, y: e.clientY });
                      }}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <MoreVertical size={18} />
                    </button>
                  </td>

                  {/* DROPDOWN PORTAL */}
                  {openActionMenu === ticket.id && (
                    <DropdownPortal>
                      <div
                        style={{
                          position: "fixed",
                          top: menuPosition.y + 10,
                          left: menuPosition.x - 160,
                          zIndex: 99999,
                        }}
                        className="w-44 bg-white border rounded-lg shadow-xl py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowApproveModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          Approve
                        </button>

                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowRejectModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          Reject
                        </button>

                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowAssignModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          Assign
                        </button>

                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowHoldModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          Hold
                        </button>
                      </div>
                    </DropdownPortal>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {rowsToShow.length > 0 && (
        <div className="flex justify-end items-center p-4">
          <div className="flex items-center gap-2">
            {/* Prev Button */}
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-100 transition"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded-lg text-sm transition ${
                  currentPage === i + 1
                    ? "bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                {i + 1}
              </button>
            ))}

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-100 transition"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* View Details of Ticket Modal */}
      <ViewTicketModal
        show={viewModal}
        onClose={() => setViewModal(false)}
        onOpenHistory={(history) => {
          setViewModal(false);
          setHistoryData(history);
          setHistoryModal(true);
        }}
        ticket={selectedTicket}
        onApprove={(ticket) => {
          setApproveTicketData(ticket);
          setShowApproveModal(true);
        }}
        onReject={(ticket) => {
          setRejectTicketData(ticket);
          setShowRejectModal(true);
        }}
        onAssign={(ticket) => {
          setAssignTicketData(ticket);
          setShowAssignModal(true);
        }}
        onHold={(ticket) => {
          setHoldTicketData(ticket);
          setShowHoldModal(true);
        }}
      />

      <TicketHistoryModal
        show={historyModal}
        onClose={() => {
          setHistoryModal(false);
          setViewModal(true);
        }}
        history={historyData}
      />

      <FilterTableModal
        show={filterModal}
        onClose={() => setFilterModal(false)}
        field={filterField}
        options={filterOptions}
        activeSelections={filters[filterField] || []}
        onApply={applyColumnFilter}
        onResetAll={() => {
          setFilters({});
          setFilteredTickets(tickets);
        }}
      />

      {/* Attach Modals */}
      <ApproveTicketModal
        show={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        ticketData={selectedTicket}
        onApproved={loadNewTickets}
      />

      <RejectTicketModal
        show={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        ticketData={selectedTicket}
        onRejected={loadNewTickets}
      />

      <AssignTicketModal
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        ticketData={selectedTicket}
        onAssigned={loadNewTickets}
      />

      <HoldTicketModal
        show={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        ticketData={selectedTicket}
        onHold={loadNewTickets}
      />
    </div>
  );
};

export default NewTickets;
